// Shared offline-recording TypeScript types (APP-6190, T019).
//
// The unions below are the frontend half of a locked vocabulary shared with
// Rust. Keep them in lockstep with their Rust source of truth:
//   - `OfflineSyncStatus`     ↔ the `sync_tracking.status` CHECK constraint in
//     `src-tauri/src/offline_recording/storage/schema_v1.sql`
//   - `OfflineConnectivityState` ↔ `ConnectivityState` in
//     `src-tauri/src/offline_recording/connectivity.rs` (snake_case serialized)
//   - `OfflineSyncItem`        ↔ `SyncTrackingRow` in
//     `src-tauri/src/offline_recording/storage/mod.rs`
//     (`#[serde(rename_all = "camelCase")]`)
// Changing one side without the other silently breaks the IPC contract.

/**
 * Every offline recording is always in exactly one of these states. Names are
 * fixed by engineering (the Rust CHECK constraint); the visual treatment is
 * owned by design (APP-6173). Order mirrors the lifecycle: capture → queued →
 * in-flight sync → terminal (synced) or halted (failure / auth).
 */
export const OFFLINE_SYNC_STATUSES = [
  'recording',
  'pending_sync',
  'creating_session',
  'uploading_audio',
  'processing',
  'synced',
  'sync_failed',
  'auth_required',
  'upload_failed',
  'unrecoverable',
] as const;

export type OfflineSyncStatus = (typeof OFFLINE_SYNC_STATUSES)[number];

/**
 * Statuses still progressing through (or waiting to enter) the sync pipeline —
 * not done, not halted. Drives the "waiting to sync" count.
 */
export const OFFLINE_PENDING_STATUSES = [
  'pending_sync',
  'creating_session',
  'uploading_audio',
  'processing',
] as const satisfies readonly OfflineSyncStatus[];

/**
 * Statuses a consult holds only while the sync engine is actively working on it.
 *
 * Mirrors `ORPHAN_IN_FLIGHT_STATUSES` in
 * `src-tauri/src/offline_recording/storage/mod.rs`, and deliberately EXCLUDES
 * `pending_sync`: a queued recording nobody has picked up yet is exactly the
 * state a clinician resumes from.
 *
 * Read by {@link useOfflineSessionCanResume} to gate resume. Rust refuses a
 * capture on a consult in one of these states because `sync::finalize_synced`
 * reclaims a consult by filename and cascades every take's row away, which
 * would delete a take recorded after the drain snapshotted its rows. These are
 * transient — the boot reset in `Storage::new` clears any left behind by a
 * crash — so a resume this blocks becomes available again on its own.
 */
export const OFFLINE_SYNC_IN_FLIGHT_STATUSES = [
  'creating_session',
  'uploading_audio',
  'processing',
] as const satisfies readonly OfflineSyncStatus[];

export const isOfflineSyncInFlightStatus = (status: OfflineSyncStatus): boolean =>
  (OFFLINE_SYNC_IN_FLIGHT_STATUSES as readonly OfflineSyncStatus[]).includes(status);

/**
 * Statuses that have stopped making progress on their own and need user
 * attention or a retry. `auth_required` is recoverable (re-provide a token)
 * but still surfaced as needing attention. Drives the "failed / needs
 * attention" count; the badge component (APP-6173) refines per-status messaging.
 */
export const OFFLINE_FAILED_STATUSES = [
  'sync_failed',
  'upload_failed',
  'auth_required',
  'unrecoverable',
] as const satisfies readonly OfflineSyncStatus[];

const OFFLINE_DELETABLE_STATUSES = [
  'pending_sync',
  'creating_session',
  'uploading_audio',
  'processing',
  ...OFFLINE_FAILED_STATUSES,
] as const satisfies readonly OfflineSyncStatus[];

export const isOfflinePendingStatus = (status: OfflineSyncStatus): boolean =>
  (OFFLINE_PENDING_STATUSES as readonly OfflineSyncStatus[]).includes(status);

export const isOfflineFailedStatus = (
  status: OfflineSyncStatus
): status is (typeof OFFLINE_FAILED_STATUSES)[number] =>
  (OFFLINE_FAILED_STATUSES as readonly OfflineSyncStatus[]).includes(status);

export const isOfflineDeletableStatus = (
  status: OfflineSyncStatus | undefined
): boolean =>
  status
    ? (OFFLINE_DELETABLE_STATUSES as readonly OfflineSyncStatus[]).includes(status)
    : false;

/**
 * Real backend-reachability state from the Rust connectivity monitor. Not
 * `navigator.onLine` — `online_ready_to_sync` means the residency-pinned
 * backend actually answered a probe. `unknown` means the monitor has not yet
 * evaluated a target, so UI must stay neutral.
 */
export const OFFLINE_CONNECTIVITY_STATES = [
  'unknown',
  'offline',
  'backend_unreachable',
  'online_ready_to_sync',
] as const;

export type OfflineConnectivityState = (typeof OFFLINE_CONNECTIVITY_STATES)[number];

/**
 * Connectivity-dependent UI state. Kept as a named alias because older FE
 * surfaces were written when `unknown` was FE-only; Rust now mirrors it as the
 * unprobed/no-target state so the contract stays explicit.
 */
export type OfflineConnectivityUiState = OfflineConnectivityState;

/**
 * Resolved, consumer-facing desktop connectivity — the single tri-state every
 * routing / account-nav surface reads (via `useDesktopConnectivity`) instead of
 * interpreting the raw `unknown` atom themselves:
 *   - `online`  — safe to hit the backend / show online-account surfaces.
 *   - `offline` — confirmed unreachable, or the browser positively reports
 *     offline (`navigator.onLine === false`). A uniform fail-safe, independent
 *     of the offline-recording rollout flag.
 *   - `pending` — not yet resolved; a probe is armed and a confirmed reading is
 *     coming. Callers must handle it explicitly (defer, don't fail open).
 */
export type DesktopConnectivity = 'online' | 'offline' | 'pending';

// `OfflineSyncItem` — the frontend mirror of the Rust `SyncTrackingRow` —
// moved to `@heidi/session/contracts` with the combined-sessions core, which
// reads it from inside the package and may not name an app path. Re-exported
// so every importer of this path keeps resolving against the one declaration.
export type { OfflineSyncItem } from '@heidi/session/contracts';
